import { create } from "zustand";
import type { FundingCall, FundingCallStatus } from "../../../engine/fundingClient";
import { API } from "@/lib/apiConfig";

const FUNDING_BASE = API.fundingRadar;

type MatchProfile = { cpvs: string[]; keywords: string[]; budgetMin?: number; budgetMax?: number };
type Relevance = { score: number; reasons: string[] };

export interface FundingRadarStoreState {
  calls: FundingCall[];
  lastSyncAt: string | null;
  syncInProgress: boolean;
  filterStatus: FundingCallStatus | "all";
  filterProgramme: string;
  filterKeyword: string;
  matchProfile: MatchProfile;
  relevanceScores: Record<string, Relevance>;
  stats: { open: number; forthcoming: number; closed: number };

  syncCalls: (filter?: { keyword?: string; programme?: string; status?: FundingCallStatus | "all" }) => Promise<void>;
  loadCachedCalls: (filter?: { status?: FundingCallStatus | "all"; programme?: string; keyword?: string; limit?: number }) => Promise<void>;
  runMatch: () => Promise<void>;
  loadStats: () => Promise<void>;
  setFilterStatus: (s: FundingCallStatus | "all") => void;
  setFilterProgramme: (p: string) => void;
  setFilterKeyword: (k: string) => void;
  setMatchProfile: (p: MatchProfile) => void;
  exportFundingList: () => void;
}

export const useFundingRadarStore = create<FundingRadarStoreState>((set, get) => ({
  calls: [],
  lastSyncAt: null,
  syncInProgress: false,
  filterStatus: "open",
  filterProgramme: "ALL",
  filterKeyword: "",
  matchProfile: { cpvs: [], keywords: [] },
  relevanceScores: {},
  stats: { open: 0, forthcoming: 0, closed: 0 },

  syncCalls: async (filter = {}) => {
    set({ syncInProgress: true });
    const u = new URL("/funding/sync", FUNDING_BASE);
    if (filter.keyword) u.searchParams.set("keyword", filter.keyword);
    if (filter.programme && filter.programme !== "ALL") u.searchParams.set("programme", filter.programme);
    if (filter.status && filter.status !== "all") u.searchParams.set("status", filter.status);
    const res = await fetch(u.toString());
    if (!res.ok) {
      set({ syncInProgress: false });
      throw new Error(`funding_sync_failed_${res.status}`);
    }
    const j = (await res.json()) as { lastSync?: string };
    set({ lastSyncAt: j.lastSync ?? new Date().toISOString(), syncInProgress: false });
    await get().loadCachedCalls({
      status: get().filterStatus,
      programme: get().filterProgramme,
      keyword: get().filterKeyword,
      limit: 100,
    });
    await get().loadStats();
  },

  loadCachedCalls: async (filter = {}) => {
    const u = new URL("/funding/list", FUNDING_BASE);
    if (filter.status && filter.status !== "all") u.searchParams.set("status", filter.status);
    if (filter.programme && filter.programme !== "ALL") u.searchParams.set("programme", filter.programme);
    if (filter.keyword) u.searchParams.set("keyword", filter.keyword);
    u.searchParams.set("limit", String(filter.limit ?? 100));
    const res = await fetch(u.toString());
    if (!res.ok) throw new Error(`funding_list_failed_${res.status}`);
    const j = (await res.json()) as { items: FundingCall[] };
    set({ calls: j.items ?? [] });
  },

  runMatch: async () => {
    const body = get().matchProfile;
    const res = await fetch(new URL("/funding/match", FUNDING_BASE).toString(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`funding_match_failed_${res.status}`);
    const j = (await res.json()) as { items: Array<{ call: FundingCall; score: number; reasons: string[] }> };
    const scores: Record<string, Relevance> = {};
    const calls = j.items.map((it) => {
      scores[it.call.callId] = { score: it.score, reasons: it.reasons };
      return it.call;
    });
    set({ calls, relevanceScores: scores });
  },

  loadStats: async () => {
    const res = await fetch(new URL("/funding/stats", FUNDING_BASE).toString());
    if (!res.ok) return;
    const j = (await res.json()) as { open: number; forthcoming: number; closed: number };
    set({ stats: j });
  },

  setFilterStatus: (s) => set({ filterStatus: s }),
  setFilterProgramme: (p) => set({ filterProgramme: p }),
  setFilterKeyword: (k) => set({ filterKeyword: k }),
  setMatchProfile: (p) => set({ matchProfile: p }),

  exportFundingList: () => {
    const payload = { exportedAt: new Date().toISOString(), calls: get().calls, relevance: get().relevanceScores };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fundingRadar_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },
}));

